import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { userLikeSchema } from './user.js';
import { paginatedRequest, paginatedResponse } from './util-schema-types.js';

export const substitutionRequestTypeSchema = z.enum(['requested_sub_by_coach', 'coach_no_show', 'assigned_by_admin']);
export type SubstitutionRequestType = z.infer<typeof substitutionRequestTypeSchema>;
export const SubstitutionRequestType = substitutionRequestTypeSchema.enum;

export const SubstitutionRequestTypeLabelMap: Record<SubstitutionRequestType, string> = {
  [SubstitutionRequestType.requested_sub_by_coach]: 'Requested by coach',
  [SubstitutionRequestType.coach_no_show]: 'Coach no show',
  [SubstitutionRequestType.assigned_by_admin]: 'Assigned by admin',
};

export const substitutionRequestStatusSchema = z.nativeEnum(PrismaEnums.SubstitutionRequestStatus);
export type SubstitutionRequestStatus = z.infer<typeof substitutionRequestStatusSchema>;
export const SubstitutionRequestStatus = substitutionRequestStatusSchema.enum;

export const substitutionRequestOriginSchema = z.enum(['manual', 'automatic_conflict']);
export type SubstitutionRequestOrigin = z.infer<typeof substitutionRequestOriginSchema>;
export const SubstitutionRequestOrigin = substitutionRequestOriginSchema.enum;

export const substitutionRequestSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  requestedById: z.string(),
  requestedByCoach: z.lazy(() => userLikeSchema),
  substituteId: z.string().nullable(),
  substituteCoach: z.lazy(() => userLikeSchema).nullable(),
  status: substitutionRequestStatusSchema,
  type: substitutionRequestTypeSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  hoursUntilSessionStart: z.number(),
  acceptedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  origin: substitutionRequestOriginSchema,
});

export type SubstitutionRequest = z.infer<typeof substitutionRequestSchema>;

export const substitutionRequestWithSessionSchema = substitutionRequestSchema.extend({
  session: z.object({
    id: z.string(),
    startInstant: z.instanceof(Temporal.Instant),
    endInstant: z.instanceof(Temporal.Instant),
    status: z.nativeEnum(PrismaEnums.SessionStatus),
    coachId: z.string().nullable(),
  }),
  lessonName: z.string(),
});

export type SubstitutionRequestWithSession = z.infer<typeof substitutionRequestWithSessionSchema>;

export const createSubstitutionRequestByCoachSchema = z.object({
  sessionId: z.string(),
});
export type CreateSubstitutionByCoachRequest = z.infer<typeof createSubstitutionRequestByCoachSchema>;

export const createSubstitutionRequestByAdminSchema = z.object({
  type: substitutionRequestTypeSchema,
  coachId: z.string(),
  sessionId: z.string(),
});
export type CreateSubstitutionRequestByAdmin = z.infer<typeof createSubstitutionRequestByAdminSchema>;

export const updateSubstitutionRequestByAdminSchema = z.object({
  status: substitutionRequestStatusSchema.optional(),
  type: substitutionRequestTypeSchema.optional(),
  substituteId: z.string().nullable().optional(),
});
export type UpdateSubstitutionRequestByAdmin = z.infer<typeof updateSubstitutionRequestByAdminSchema>;

export const substitutionRequestIdSchema = z.object({
  subRequestId: z.string(),
});

export const getSubstitutionRequestsQuery = paginatedRequest.extend({
  startOnOrBefore: z.string().optional(),
  startOnOrAfter: z.string().optional(),
  statuses: z.string().optional(),
});
export type GetSubstitutionRequestsQuery = z.infer<typeof getSubstitutionRequestsQuery>;

export const getSubstitutionRequestsResultsSchema = paginatedResponse.extend({
  substitutionRequests: z.array(substitutionRequestWithSessionSchema),
});

export type GetSubstitutionRequestsResults = z.infer<typeof getSubstitutionRequestsResultsSchema>;

export type CoachSubstituionInvolvement =
  | 'requested_req_pending'
  | 'requested_req_fulfilled'
  | 'fulfilled_request'
  | 'none';
